import type {
  AdminDTO,
  AdminSareeListQuery,
  CategoryDTO,
  CategoryInput,
  Paginated,
  PublicConfig,
  SareeDTO,
  SareeFacets,
  SareeInput,
  SareeListQuery,
} from '@neyara/shared';
import { keepPreviousData, QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError, withQuery } from './api';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      retry: (count, err) => !(err instanceof ApiError && err.status < 500) && count < 2,
      refetchOnWindowFocus: false,
    },
  },
});

// ---------- storefront ----------

export const useConfig = () =>
  useQuery({ queryKey: ['config'], queryFn: () => api<PublicConfig>('/config'), staleTime: Infinity });

export const useCategories = () =>
  useQuery({ queryKey: ['categories'], queryFn: () => api<CategoryDTO[]>('/categories') });

export const useFacets = () => useQuery({ queryKey: ['facets'], queryFn: () => api<SareeFacets>('/sarees/facets') });

export const useSarees = (query: Partial<SareeListQuery>) =>
  useQuery({
    queryKey: ['sarees', query],
    queryFn: () => api<Paginated<SareeDTO>>(withQuery('/sarees', query)),
    placeholderData: keepPreviousData,
  });

export const useSaree = (slug: string | undefined) =>
  useQuery({
    queryKey: ['saree', slug],
    queryFn: () => api<SareeDTO>(`/sarees/${encodeURIComponent(slug!)}`),
    enabled: Boolean(slug),
  });

// ---------- admin ----------

export const useMe = () =>
  useQuery({
    queryKey: ['me'],
    queryFn: () => api<AdminDTO>('/auth/me'),
    retry: false,
    staleTime: 5 * 60_000,
  });

export function useLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { email: string; password: string }) => api<AdminDTO>('/auth/login', { method: 'POST', json: body }),
    onSuccess: (admin) => qc.setQueryData(['me'], admin),
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api<void>('/auth/logout', { method: 'POST' }),
    onSuccess: () => {
      qc.setQueryData(['me'], null);
      qc.removeQueries({ queryKey: ['admin'] });
    },
  });
}

export const useChangePassword = () =>
  useMutation({
    mutationFn: (body: { currentPassword: string; newPassword: string }) =>
      api<void>('/auth/password', { method: 'POST', json: body }),
  });

export const useAdminSarees = (query: Partial<AdminSareeListQuery>) =>
  useQuery({
    queryKey: ['admin', 'sarees', query],
    queryFn: () => api<Paginated<SareeDTO>>(withQuery('/admin/sarees', query)),
    placeholderData: keepPreviousData,
  });

export const useAdminSaree = (id: string | undefined) =>
  useQuery({
    queryKey: ['admin', 'saree', id],
    queryFn: () => api<SareeDTO>(`/admin/sarees/${id}`),
    enabled: Boolean(id),
  });

/** Anything that changes the catalog invalidates both admin and storefront caches. */
function useCatalogMutation<TVars, TResult>(fn: (vars: TVars) => Promise<TResult>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () =>
      Promise.all(
        ['admin', 'sarees', 'saree', 'facets', 'categories'].map((key) => qc.invalidateQueries({ queryKey: [key] })),
      ),
  });
}

export const useSaveSaree = () =>
  useCatalogMutation(({ id, input }: { id?: string; input: SareeInput }) =>
    id
      ? api<SareeDTO>(`/admin/sarees/${id}`, { method: 'PUT', json: input })
      : api<SareeDTO>('/admin/sarees', { method: 'POST', json: input }),
  );

export const useUpdateStock = () =>
  useCatalogMutation(({ id, stock }: { id: string; stock: number }) =>
    api<SareeDTO>(`/admin/sarees/${id}/stock`, { method: 'PATCH', json: { stock } }),
  );

export const useDeleteSaree = () =>
  useCatalogMutation((id: string) => api<void>(`/admin/sarees/${id}`, { method: 'DELETE' }));

export const useSaveCategory = () =>
  useCatalogMutation(({ id, input }: { id?: string; input: CategoryInput }) =>
    id
      ? api<CategoryDTO>(`/admin/categories/${id}`, { method: 'PUT', json: input })
      : api<CategoryDTO>('/admin/categories', { method: 'POST', json: input }),
  );

export const useDeleteCategory = () =>
  useCatalogMutation((id: string) => api<void>(`/admin/categories/${id}`, { method: 'DELETE' }));
